import { Component, OnInit } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';

import 'src/core/services/auth/oauth-service.extensions';
import { environment } from 'src/environments/environment';
import {
  appendEmbeddedMode,
  clearExternalAuthContext,
  enableExternalView,
  getExternalAuthContext,
  hasEmbeddedModeQuery,
  resolveInternalRedirect,
  setExternalAuthContext,
} from './external-view-state';

type ExternalAuthContext = {
  embedded: boolean;
  externalUrl: string;
  language: string;
  loginHint: string;
  orgId: string;
  redirectTo: string;
  refreshToken: string;
  tenant: string;
};

const AZURE_B2C_EMBEDDED_PROVIDER = 'Azure B2C Embedded';

@Component({
  selector: 'app-root',
  templateUrl: './external-auth-bootstrap.component.html',
  styleUrls: ['./external-auth-bootstrap.component.scss'],
  standalone: false,
})
export class ExternalAuthBootstrapComponent implements OnInit {
  message = 'Completing sign in...';

  constructor(private readonly oAuthService: OAuthService) {}

  async ngOnInit(): Promise<void> {
    enableExternalView();

    try {
      await this.run();
    } catch (error) {
      console.error('External auth bootstrap failed.', error);
      this.message = 'Authentication failed.';
    }
  }

  private async run(): Promise<void> {
    const requestContext = this.getContext();
    this.oAuthService.configure(environment.oAuthConfig);
    await this.oAuthService.loadDiscoveryDocument();

    if (this.oAuthService.hasValidAccessToken()) {
      this.redirectToApp(requestContext);
      return;
    }

    if (this.hasAuthorizationResponse()) {
      this.message = 'Finalizing authorization...';
      await this.oAuthService.tryLoginCodeFlow();

      if (this.oAuthService.hasValidAccessToken()) {
        this.redirectToApp(this.getContextFromState());
        return;
      }
    }

    if (requestContext.refreshToken) {
      this.message = 'Refreshing session...';
      await this.oAuthService.fetchTokenUsingGrant('refresh_token', {
        refresh_token: requestContext.refreshToken,
      });
      this.redirectToApp(requestContext);
      return;
    }

    if (requestContext.loginHint) {
      this.message = 'Redirecting to identity provider...';
      window.location.replace(
        this.buildLoginHintChallengeUrl(
          requestContext.loginHint,
          requestContext.orgId,
          requestContext.tenant,
          this.buildReturnUrl(
            requestContext.redirectTo,
            requestContext.language,
            requestContext.tenant,
            requestContext.embedded
          )
        )
      );
      return;
    }

    this.message = 'Redirecting to sign in...';
    setExternalAuthContext(requestContext.externalUrl);
    this.oAuthService.initCodeFlow(requestContext.externalUrl);
  }

  private getContext(): ExternalAuthContext {
    const externalUrl =
      window.location.pathname.startsWith('/external')
        ? window.location.pathname + window.location.search
        : getExternalAuthContext() ?? '/external';
    const url = new URL(externalUrl, window.location.origin);

    return {
      embedded: hasEmbeddedModeQuery(url.search),
      externalUrl,
      language: url.searchParams.get('lang') ?? '',
      loginHint: url.searchParams.get('loginHint') ?? '',
      orgId: url.searchParams.get('orgId') ?? '',
      redirectTo: url.searchParams.get('redirectTo') ?? '',
      refreshToken: url.searchParams.get('refreshToken') ?? '',
      tenant:
        url.searchParams.get('__tenant') ??
        url.searchParams.get('tenantId') ??
        url.searchParams.get('tenant') ??
        '',
    };
  }

  private getContextFromState(): ExternalAuthContext {
    const externalUrl = getExternalAuthContext() || this.oAuthService.state || '/external';
    const url = new URL(externalUrl, window.location.origin);

    return {
      embedded: hasEmbeddedModeQuery(url.search),
      externalUrl,
      language: url.searchParams.get('lang') ?? '',
      loginHint: url.searchParams.get('loginHint') ?? '',
      orgId: url.searchParams.get('orgId') ?? '',
      redirectTo: url.searchParams.get('redirectTo') ?? '',
      refreshToken: url.searchParams.get('refreshToken') ?? '',
      tenant:
        url.searchParams.get('__tenant') ??
        url.searchParams.get('tenantId') ??
        url.searchParams.get('tenant') ??
        '',
    };
  }

  private hasAuthorizationResponse(): boolean {
    const query = new URLSearchParams(window.location.search);
    return query.has('code') || query.has('error');
  }

  private buildReturnUrl(
    redirectTo: string,
    language: string,
    tenant: string,
    embedded: boolean
  ): string {
    const params = new URLSearchParams();

    if (redirectTo) {
      params.set('redirectTo', redirectTo);
    }

    if (language) {
      params.set('lang', language);
    }

    if (tenant) {
      params.set('tenantId', tenant);
    }

    appendEmbeddedMode(params, embedded);

    const queryString = params.toString();
    return `${window.location.origin}/external${queryString ? `?${queryString}` : ''}`;
  }

  private buildLoginHintChallengeUrl(
    loginHint: string,
    orgId: string,
    tenant: string,
    returnUrl: string
  ): string {
    const authServerBaseUrl = this.oAuthService.tokenEndpoint.replace('/connect/token', '');
    const params = new URLSearchParams({
      handler: 'ExternalLogin',
      provider: AZURE_B2C_EMBEDDED_PROVIDER,
      login_hint: loginHint,
      ReturnUrl: returnUrl,
    });

    if (tenant) {
      params.set('tenantId', tenant);
    }

    if (orgId) {
      params.set('selectedOrgId', orgId);
    }

    return `${authServerBaseUrl}/Account/Login?${params.toString()}`;
  }

  private redirectToApp(context: ExternalAuthContext): void {
    clearExternalAuthContext();

    const url = resolveInternalRedirect(context.redirectTo);

    if (context.language) {
      url.searchParams.set('lang', context.language);
    }

    if (context.tenant) {
      url.searchParams.set('__tenant', context.tenant);
    }

    appendEmbeddedMode(url.searchParams, context.embedded);

    window.location.replace(`${url.pathname}${url.search}${url.hash}`);
  }
}
